import { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Menu } from 'lucide-react';
import {
  Store,
  FileText,
  ChevronDown,
  ChevronRight,
  LogOut,
  BarChart
} from 'lucide-react';
import {
  useAppSelector,
  useAppDispatch,
} from '../../store/hooks/reduxHooks';
import { handleLogout } from '../../utils/sessionManager';
import { Permission } from '../../types/auth';
import blackLogo from '../../assets/CleverPe Logos/CleverPe_Black_H.svg';

type SidebarItem = {
  title: string;
  path: string;
  icon: React.ReactNode;
  requiredPermission?: Permission;
  alwaysVisible?: boolean;
};

type SidebarSection = {
  title: string;
  items: SidebarItem[];
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.auth);


  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const [expandedSections, setExpandedSections] = useState<string[]>([
    'Transactions',
    'Merchant Management',
    'Reports',
  ]);

  // Check if user has required permission
  const hasPermission = (permission?: Permission): boolean => {
    if (!permission) return true;

    if (!user?.dashPermissions) return false;

    return user.dashPermissions.includes(permission);
  };

  // Sidebar data
  const sidebarData = useMemo<SidebarSection[]>(
    () => [
      {
        title: 'Transactions',
        items: [
          {
            title: 'Orders',
            path: '/orders',
            icon: <FileText size={16} />,
            alwaysVisible: true,
          },
        ],
      },
      {
        title: 'Merchant Management',
        items: [
          {
            title: 'Merchant Approval',
            path: '/merchant-approval',
            icon: <Store size={16} />,
            requiredPermission: 'view-merchant-approval',
          },
        ],
      },
      {
        title: 'Reports',
        items: [
          {
            title: 'Settlement Report',
            path: '/settlement-report',
            icon: <BarChart size={16} />,
            requiredPermission: 'view-settlement-report',
          },
        ],
      },
    ],
    []
  );

  const toggleSection = (title: string) => {
    setExpandedSections((prev) =>
      prev.includes(title)
        ? prev.filter((s) => s !== title)
        : [...prev, title]
    );
  };

  const isActiveRoute = (path: string) => {
    return (
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`) ||
      location.pathname.endsWith(path.split('/').pop() || '')
    );
  };

  const handleNavigation = (path: string) => {
    navigate(path);

    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const handleLogoutClick = () => {
    handleLogout(dispatch);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile toggle button */}
      <button
        className="fixed z-30 p-2 bg-white rounded-md shadow-md top-4 left-4 lg:hidden"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-20 w-64 bg-white shadow-xl transition-transform duration-300 transform
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 flex flex-col h-screen`}
      >
        {/* Header */}
        <div className="flex items-center justify-center h-16 py-2 border-b shrink-0">
          <img
            src={blackLogo}
            alt="CleverPe"
            className="h-10"
          />
        </div>

        {/* Scrollable section */}
        <div
          className="overflow-y-auto no-scrollbar flex-1 px-3 py-2"
          style={{
            height: 'calc(100vh - 64px - 56px)',
          }}
        >
          <nav>
            {sidebarData.map((section) => {
              // Only show items the user is allowed to see.
              // Orders is always visible because alwaysVisible=true.
              const visibleItems = section.items.filter(
                (item) =>
                  item.alwaysVisible ||
                  hasPermission(item.requiredPermission)
              );

              if (visibleItems.length === 0) {
                return null;
              }

              return (
                <div
                  key={section.title}
                  className="mb-3"
                >
                  {/* Section header */}
                  <button
                    className="flex items-center justify-between w-full mb-1 text-left"
                    onClick={() =>
                      toggleSection(section.title)
                    }
                  >
                    <h3 className="text-xs tracking-wider uppercase font-gilroy-bold text-brand-gray">
                      {section.title}
                    </h3>

                    {expandedSections.includes(
                      section.title
                    ) ? (
                      <ChevronDown
                        size={14}
                        className="text-brand-gray"
                      />
                    ) : (
                      <ChevronRight
                        size={14}
                        className="text-brand-gray"
                      />
                    )}
                  </button>

                  {/* Items */}
                  {expandedSections.includes(section.title) && (
                    <ul className="space-y-0.5 mb-2">
                      {visibleItems.map((item) => (
                        <li key={item.path}>
                          <button
                            className={`flex items-center w-full px-3 py-2 rounded-lg transition-colors duration-200 text-sm
                            ${isActiveRoute(item.path)
                                ? 'bg-brand-light text-brand-blue font-gilroy-medium'
                                : 'text-brand-dark hover:bg-gray-100 font-gilroy-regular'
                              }`}
                            onClick={() =>
                              handleNavigation(item.path)
                            }
                          >
                            <span className="mr-2.5">
                              {item.icon}
                            </span>

                            {item.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.title !==
                    sidebarData[
                      sidebarData.length - 1
                    ].title && (
                      <div className="my-1 border-b border-gray-200" />
                    )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Footer */}
        <div className="p-3 border-t shrink-0 h-14">
          <button
            onClick={handleLogoutClick}
            className="flex items-center w-full px-3 py-2 text-sm text-red-600 transition-colors duration-200 rounded-lg hover:bg-red-50"
          >
            <span className="mr-2.5">
              <LogOut size={16} />
            </span>
            Logout
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;